// Star field vertex shader — one point sprite per catalogue entry.
//
// `position` holds the star's J2000 equatorial unit vector straight from the
// catalogue; the celestial orientation (latitude, sidereal time, compass
// alignment) rides in the object's model matrix, so the sky can be re-aimed
// without touching the buffers. StarField parks the object at the camera each
// frame, which makes the sprite distance constant and the sky unreachable.
//
// Note the logdepthbuf chunks: the renderer uses logarithmicDepthBuffer, so
// this in-scene material must emit matching depth or terrain would fail to
// occlude the stars sitting behind it.

#include <common>
#include <logdepthbuf_pars_vertex>

attribute float aMag;    // visual magnitude
attribute vec3 aColor;   // linear RGB from the star's B−V colour index

uniform float uRadius;
uniform float uPixelRatio;
uniform float uTime;

uniform float uMagRef;           // magnitude that renders at unit flux
uniform float uSizeMin;          // px — floor, so the faintest stars stay resolvable
uniform float uSizeMax;          // px — ceiling, so Sirius stays a star not a blob
uniform float uSizeScale;
uniform float uSizeGamma;
uniform float uBrightness;
uniform float uBrightnessGamma;

uniform float uTwinkle;          // scintillation depth, 0 = off
uniform float uTwinkleSpeed;
uniform float uTwinkleHorizon;   // extra scintillation gain at the horizon

uniform float uFadeLow;          // sin(alt) where extinction is total
uniform float uFadeHigh;         // sin(alt) where extinction has cleared
uniform float uOpacity;          // master fade

varying vec3 vColor;

void main() {
  vec3 dirEq = normalize(position);
  gl_Position = projectionMatrix * modelViewMatrix * vec4(dirEq * uRadius, 1.0);

  // Altitude above the horizon, in world space — the model matrix is a pure
  // rotation about the observer, so its 3×3 part maps catalogue → world.
  float sinAlt = normalize(mat3(modelMatrix) * dirEq).y;

  // Pogson's ratio: five magnitudes is a factor of 100 in flux, so a star at
  // uMagRef carries unit flux and each magnitude fainter is 2.512× dimmer.
  float flux = pow(10.0, -0.4 * (aMag - uMagRef));

  // Real stars are point sources that only vary in brightness, but a display
  // pixel clips long before the catalogue's 25-stop range is spent. So the
  // flux is split: part buys sprite area, the rest buys intensity. Both
  // exponents are art knobs — pure intensity would lose everything past ~4th
  // magnitude and blow the top end into white discs.
  gl_PointSize = clamp(uSizeMin + uSizeScale * pow(flux, uSizeGamma), uSizeMin, uSizeMax) * uPixelRatio;

  float amp = uBrightness * pow(flux, uBrightnessGamma);

  // Atmospheric extinction — the long slant path near the horizon swallows
  // low stars. Doubles as insurance that nothing draws against distant terrain
  // right at the far plane, where the sky sphere and the ground meet.
  float ext = smoothstep(uFadeLow, uFadeHigh, sinAlt);

  // Scintillation, strongest low down where there is most air to shimmer
  // through. The hash decorrelates neighbours so the field twinkles instead of
  // pulsing in unison.
  float phase = fract(sin(dot(dirEq, vec3(12.9898, 78.233, 37.719))) * 43758.5453) * PI2;
  float horizonGain = mix(uTwinkleHorizon, 1.0, smoothstep(0.0, 0.5, sinAlt));
  float tw = 1.0 + uTwinkle * horizonGain * sin(uTime * uTwinkleSpeed + phase);

  vColor = aColor * (amp * ext * max(tw, 0.0) * uOpacity);

  #include <logdepthbuf_vertex>
}
